'use client';

import Link from 'next/link';
import { Anchor, Badge, Container, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { getCarComparisonLinkProps } from '../../../lib/content/comparisonLinks';
import type { PageWidgetItem } from '../../../types/content';
import {
  getWidgetCardStyle,
  widgetBadgeStyle,
  widgetLinkStyle,
  widgetSectionPadding,
  widgetSubtitleStyle,
  widgetTitleStyle,
} from './widgetStyles';

export function FeatureCardsWidget({
  badge,
  title,
  subtitle,
  items,
  mapHref,
}: {
  badge?: string;
  title?: string;
  subtitle?: string;
  items: PageWidgetItem[];
  mapHref: (href?: string) => string | undefined;
}) {
  if (!items.length && !title && !subtitle) return null;

  return (
    <Container size="xl" py={widgetSectionPadding}>
      <Stack gap={16}>
        {badge ? (
          <Badge variant="light" radius="xl" style={widgetBadgeStyle}>
            {badge}
          </Badge>
        ) : null}

        {title ? <Title order={2} style={widgetTitleStyle}>{title}</Title> : null}

        {subtitle ? <Text style={widgetSubtitleStyle}>{subtitle}</Text> : null}

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing={20}>
          {items.map((item, index) => {
            const href = mapHref(item.href);
            return (
              <Paper
                key={`${item.title || 'card'}-${index}`}
                p={22}
                radius={30}
                withBorder
                style={getWidgetCardStyle(index, { emphasize: index === 0 })}
              >
                <Stack gap={10}>
                  <Text fw={600} style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                    {item.title || item.label || `Card ${index + 1}`}
                  </Text>
                  {item.body ? <Text size="sm" style={{ ...widgetSubtitleStyle, maxWidth: 'unset' }}>{item.body}</Text> : null}
                  {href ? (
                    <Anchor
                      component={Link}
                      href={href}
                      {...getCarComparisonLinkProps(href)}
                      underline="never"
                      style={widgetLinkStyle}
                    >
                      Lue lisää
                    </Anchor>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
